import React, { Component } from "react";
import Modal from "reactstrap/lib/Modal";
import ModalHeader from "reactstrap/lib/ModalHeader";
import ModalBody from "reactstrap/lib/ModalBody";
import ModalFooter from "reactstrap/lib/ModalFooter";
import Button from "reactstrap/lib/Button";

const POLYGON_OPTIONS = { fillColor: "#f1061c", fillOpacity: 0.35, strokeColor: "#000000", strokeOpacity: 1, strokeWeight: 2, editable: true };

// Draws/edits a polygon geofence using a plain google.maps.Map + an editable
// google.maps.Polygon, adding a vertex on each map click. NOT built on
// google.maps.drawing.DrawingManager — Google removed DrawingManager as of
// Maps JS API v3.65 (confirmed via a live repro: constructing it throws
// "The DrawingManager functionality ... is no longer available"). This is
// Google's own current recommended workaround: push each click's latLng
// onto the polygon's editable MVCArray path directly. Right-click on a
// vertex still removes it, and dragging still repositions it — both are
// native Polygon/editable behavior, unrelated to DrawingManager.
class FieldLocationPolygonMap extends Component {
  mapContainerRef = React.createRef();
  searchInputRef = React.createRef();
  map = null;
  overlay = null; // the google.maps.Polygon being built/edited
  mapClickListener = null;
  searchAutocomplete = null;
  state = { manualLat: "", manualLng: "" };

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      // Modal unmounts its content on close, so the container ref only exists
      // once the open transition has actually rendered it.
      setTimeout(this.initMap, 200);
    }
    if (!this.props.open && prevProps.open) {
      this.teardown();
    }
  }

  componentWillUnmount() {
    this.teardown();
  }

  teardown = () => {
    if (this.mapClickListener) {
      window.google.maps.event.removeListener(this.mapClickListener);
      this.mapClickListener = null;
    }
    if (this.overlay) {
      this.overlay.setMap(null);
      this.overlay = null;
    }
    this.map = null;
    this.searchAutocomplete = null;
    this.setState({ manualLat: "", manualLng: "" });
  };

  initMap = () => {
    if (!this.mapContainerRef.current || !window.google) return;
    const initialPoints = (this.props.initialPoints || []).map(p => ({ lat: Number(p.lat), lng: Number(p.lng) }));
    const center = this.props.initialCenter || { lat: 19.076, lng: 72.8777 };
    this.map = new window.google.maps.Map(this.mapContainerRef.current, {
      center,
      zoom: initialPoints.length ? 16 : 14
    });

    // Passing paths: [] (rather than omitting the key) breaks the Polygon's
    // internal editable-overlay setup — confirmed via a live repro: it throws
    // an internal Maps error at construction and every map click silently
    // stops registering afterward, map panning unaffected. Omitting the key
    // when there's nothing to seed leaves Polygon to create a normal empty
    // path on its own, which is the standard "click to build a polygon" recipe.
    this.overlay = initialPoints.length
      ? new window.google.maps.Polygon({ paths: initialPoints, ...POLYGON_OPTIONS })
      : new window.google.maps.Polygon(POLYGON_OPTIONS);
    this.overlay.setMap(this.map);
    ["insert_at", "remove_at", "set_at"].forEach(evt => window.google.maps.event.addListener(this.overlay.getPath(), evt, () => this.forceUpdate()));

    // Editable Polygon does NOT remove a vertex on right-click by itself —
    // confirmed via a live repro that this needs an explicit listener.
    // The polygon (not the map) fires 'rightclick' with e.vertex set to the
    // clicked vertex's index when the click landed on a vertex handle
    // specifically (undefined for a right-click elsewhere on the shape).
    window.google.maps.event.addListener(this.overlay, "rightclick", e => {
      if (e.vertex !== undefined) this.overlay.getPath().removeAt(e.vertex);
    });

    this.mapClickListener = window.google.maps.event.addListener(this.map, "click", e => {
      this.overlay.getPath().push(e.latLng);
    });

    // Search box only re-centers the map — it never adds a point itself, so
    // searching somewhere doesn't accidentally commit an unwanted vertex.
    if (this.searchInputRef.current) {
      this.searchAutocomplete = new window.google.maps.places.Autocomplete(this.searchInputRef.current);
      this.searchAutocomplete.setFields(["geometry"]);
      this.searchAutocomplete.bindTo("bounds", this.map);
      this.searchAutocomplete.addListener("place_changed", () => {
        const place = this.searchAutocomplete.getPlace();
        if (place && place.geometry && place.geometry.location) {
          this.map.setCenter(place.geometry.location);
          this.map.setZoom(15);
        }
      });
    }

    this.forceUpdate();
  };

  handleClearRedraw = () => {
    if (this.overlay) this.overlay.getPath().clear();
    this.forceUpdate();
  };

  handleAddManualPoint = () => {
    const lat = Number(this.state.manualLat);
    const lng = Number(this.state.manualLng);
    if (!this.overlay || this.state.manualLat === "" || this.state.manualLng === "" || isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return;
    this.overlay.getPath().push(new window.google.maps.LatLng(lat, lng));
    this.map.panTo({ lat, lng });
    this.setState({ manualLat: "", manualLng: "" });
  };

  handleSave = () => {
    if (!this.overlay) return;
    const points = this.overlay
      .getPath()
      .getArray()
      .map(latLng => ({ lat: latLng.lat(), lng: latLng.lng() }));
    this.props.onSave(points);
  };

  render() {
    const { open, onClose } = this.props;
    const pointCount = this.overlay ? this.overlay.getPath().getLength() : 0;
    return (
      <Modal isOpen={!!open} toggle={onClose} size="lg">
        <ModalHeader toggle={onClose}>Draw Assigned Area</ModalHeader>
        <ModalBody>
          <p className="text-muted mb-2" style={{ fontSize: 13 }}>
            Click on the map to place each boundary point (at least 3). Drag a point to reposition it, right-click a point to remove it.
          </p>
          <input
            ref={this.searchInputRef}
            type="text"
            placeholder="Search a location to center the map"
            className="form-control mb-2"
            style={{ fontSize: 13 }}
          />
          <div ref={this.mapContainerRef} style={{ width: "100%", height: 420 }} />
          <div className="d-flex align-items-center mt-2" style={{ gap: 8, fontSize: 13 }}>
            <span className="text-muted" style={{ whiteSpace: "nowrap" }}>Add point by coordinates:</span>
            <input
              type="number"
              step="any"
              placeholder="Latitude"
              value={this.state.manualLat}
              onChange={(e) => this.setState({ manualLat: e.target.value })}
              className="form-control"
              style={{ width: 110 }}
            />
            <input
              type="number"
              step="any"
              placeholder="Longitude"
              value={this.state.manualLng}
              onChange={(e) => this.setState({ manualLng: e.target.value })}
              className="form-control"
              style={{ width: 110 }}
            />
            <Button size="sm" color="secondary" type="button" onClick={this.handleAddManualPoint}>Add Point</Button>
          </div>
          <div className="text-muted mt-2" style={{ fontSize: 12 }}>{pointCount} point{pointCount === 1 ? "" : "s"} set</div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={this.handleClearRedraw}>Clear</Button>
          <Button color="secondary" onClick={onClose}>Cancel</Button>
          <Button color="danger" disabled={pointCount < 3} onClick={this.handleSave}>Save Shape</Button>
        </ModalFooter>
      </Modal>
    );
  }
}

export default FieldLocationPolygonMap;
